import React, { useState, useEffect, useRef } from 'react'
import { formatPriceForInput, formatTakePriceForInput, parsePriceInput, priceToMillions } from '../../utils/formatters'

interface PriceInputProps {
  value: number | null | undefined
  onChange: (value: number | null) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  /** If true, displays up to 3 decimals without trailing zeros (for take prices) */
  flexibleDecimals?: boolean
}

/**
 * A price input component for entering prices in millions.
 *
 * Storage format: Full numbers (e.g., 37000000 for $37M)
 * Input format: User enters millions (e.g., "37" or "1.234")
 * Display format: Shows formatted value (e.g., "$37M" or "$1.234M")
 *
 * Example:
 * - User types "37" → displays "$37M" → stores 37000000
 * - User types "1.234" → displays "$1.234M" → stores 1234000
 */
export const PriceInput: React.FC<PriceInputProps> = ({
  value,
  onChange,
  placeholder = 'Enter price in millions (e.g., 1.234)',
  className = '',
  disabled = false,
  flexibleDecimals = false
}) => {
  const [displayValue, setDisplayValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Choose formatter based on flexibleDecimals prop
  const formatForDisplay = flexibleDecimals ? formatTakePriceForInput : formatPriceForInput

  // Update display value when prop value changes (and not focused)
  useEffect(() => {
    if (!isFocused) {
      if (value !== null && value !== undefined) {
        setDisplayValue(formatForDisplay(value))
      } else {
        setDisplayValue('')
      }
    }
  }, [value, isFocused, formatForDisplay])

  const handleFocus = () => {
    setIsFocused(true)
    // Show value in millions when focused for editing
    const millions = priceToMillions(value)
    if (millions !== null) {
      // Format without trailing zeros for cleaner editing
      let formatted = millions.toFixed(3).replace(/\.?0+$/, '')
      setDisplayValue(formatted)
    }
  }

  const handleBlur = () => {
    setIsFocused(false)
    // Parse and format on blur
    const parsed = parsePriceInput(displayValue)
    onChange(parsed)

    // Format for display
    if (parsed !== null) {
      setDisplayValue(formatForDisplay(parsed))
    } else {
      setDisplayValue('')
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    setDisplayValue(input)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // On Enter, trigger blur to format
    if (e.key === 'Enter') {
      inputRef.current?.blur()
    }
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      className={className || 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent'}
    />
  )
}

export default PriceInput

